import { useState, useEffect } from "react";
import { Calendar } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar as CalendarComponent } from "@/components/ui/calendar";
import { format } from "date-fns";
import type { Card, NoteType } from "@/types/board";
import { supabase } from "@/lib/supabase";
import { boardEvents, BOARD_UPDATED } from "@/lib/events";
import { EnhancedNoteEditor } from "@/components/EnhancedNoteEditor";

interface EditCardDialogProps {
    card: Card;
    isOpen: boolean;
    onOpenChange: (open: boolean) => void;
    onUpdate?: (cardId: string, title: string, description: string) => void;
    boardTitle?: string;
}

// Converts plain-text notes (with [timestamp]\ntext format) to HTML for TipTap
const convertToHtml = (text: string): string => {
    if (!text) return '';
    // If it already looks like HTML, pass it through as-is
    if (/<[a-z][\s\S]*>/i.test(text)) return text;
    // Convert plain text: split on double newlines for paragraphs
    return text
        .split(/\n\n+/)
        .filter(Boolean)
        .map(block => `<p>${block.replace(/\n/g, '<br/>')}</p>`)
        .join('');
};

export function EditCardDialog({
    card,
    isOpen,
    onOpenChange,
    onUpdate,
    boardTitle,
}: EditCardDialogProps) {
    const [editTitle, setEditTitle] = useState(card.title ?? '');
    const [editDescription, setEditDescription] = useState("");
    const [noteType, setNoteType] = useState<NoteType>(card.note_type ?? "general");
    const [dueDate, setDueDate] = useState<Date | undefined>(
        card.due_date ? new Date(card.due_date) : undefined
    );
    const [isDatePickerOpen, setIsDatePickerOpen] = useState(false);

    useEffect(() => {
        if (isOpen) {
            setEditTitle(card.title ?? 'Untitled Card');
            setEditDescription(convertToHtml(card.description ?? ""));
            setNoteType(card.note_type ?? "general");
            setDueDate(card.due_date ? new Date(card.due_date) : undefined);
        }
    }, [isOpen, card]);

    const handleEdit = async () => {
        if (!editTitle.trim()) return;

        try {
            const newDescription = editDescription;

            const { error: updateError } = await supabase
                .from('cards')
                .update({
                    title: editTitle.trim(),
                    description: newDescription,
                    note_type: noteType,
                    due_date: dueDate?.toISOString() ?? null,
                    updated_at: new Date().toISOString()
                })
                .eq('id', card.id);

            if (updateError) {
                console.error('Error updating card:', updateError);
                return;
            }

            if (onUpdate) {
                onUpdate(card.id, editTitle.trim(), newDescription);
            }

            setEditDescription(convertToHtml(card.description ?? ""));
            onOpenChange(false);
            boardEvents.emit(BOARD_UPDATED);

        } catch (error) {
            console.error('Error editing card:', error);
        }
    };

    return (
        <Dialog open={isOpen} onOpenChange={onOpenChange}>
            <DialogContent className="w-[95vw] max-w-lg max-h-[90vh] flex flex-col overflow-hidden rounded-xl">
                <DialogHeader>
                    <div className="flex items-center justify-between gap-4 pr-8">
                        <DialogTitle>Edit Card</DialogTitle>
                        {boardTitle && (
                            <span className="text-xs font-medium px-2 py-1 rounded-full bg-indigo-500/10 text-indigo-400 border border-indigo-500/20 whitespace-nowrap">
                                {boardTitle}
                            </span>
                        )}
                    </div>
                </DialogHeader>
                <div className="grid gap-4 py-4 flex-1 overflow-hidden">
                    <div className="space-y-2">
                        <Label htmlFor="title">Title</Label>
                        <Input
                            id="title"
                            placeholder="Enter card title"
                            value={editTitle}
                            onChange={(e) => setEditTitle(e.target.value)}
                        />
                    </div>

                    <div className="space-y-2">
                        <Label>Due Date</Label>
                        <Popover open={isDatePickerOpen} onOpenChange={setIsDatePickerOpen}>
                            <PopoverTrigger asChild>
                                <Button
                                    variant="outline"
                                    className={`w-full justify-start text-left font-normal ${!dueDate && "text-muted-foreground"}`}
                                >
                                    <Calendar className="mr-2 h-4 w-4" />
                                    {dueDate ? format(dueDate, "PPP") : "Set due date"}
                                </Button>
                            </PopoverTrigger>
                            <PopoverContent className="w-auto p-0" align="start">
                                <CalendarComponent
                                    mode="single"
                                    selected={dueDate}
                                    onSelect={(date) => {
                                        setDueDate(date);
                                        setIsDatePickerOpen(false);
                                    }}
                                    initialFocus
                                />
                            </PopoverContent>
                        </Popover>
                    </div>

                    <div className="overflow-y-auto pr-2">
                        <EnhancedNoteEditor
                            value={editDescription}
                            onChange={setEditDescription}
                            noteType={noteType}
                            onNoteTypeChange={setNoteType}
                        />
                    </div>
                </div>
                <DialogFooter className="border-t bg-background py-2">
                    <Button onClick={handleEdit}>Save Changes</Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
